import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { trackArtworkView, trackCartAdd } from '@/hooks/useAnalytics';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ArtworkDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeaders, isArtist } = useAuth();
  const { addToCart } = useCart();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorited, setIsFavorited] = useState(false);

  useEffect(() => {
    fetchArtwork();
  }, [id]);

  const fetchArtwork = async () => {
    try {
      const response = await axios.get(`${API}/artworks/${id}`);
      setArtwork(response.data);
      trackArtworkView(response.data.id, response.data.title);
    } catch (error) {
      console.error('Failed to fetch artwork:', error);
      toast.error('Artwork not found');
      navigate('/gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (addToCart(artwork)) {
      trackCartAdd(artwork.id, artwork.title);
      toast.success('Added to cart');
    } else {
      toast.error('Already in cart');
    }
  };

  const handleToggleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to add favorites');
      return;
    }

    try {
      if (isFavorited) {
        await axios.delete(`${API}/favorites/${artwork.id}`, getAuthHeaders());
        setIsFavorited(false);
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites`, { artwork_id: artwork.id }, getAuthHeaders());
        setIsFavorited(true);
        toast.success('Added to favorites');
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      toast.error('Failed to update favorites');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!artwork) return null;

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="artwork-detail-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-8 hover:translate-x-[-4px] transition-transform"
          data-testid="back-btn"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          BACK
        </Button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          <div className="aspect-[3/4] overflow-hidden bg-muted">
            <img
              src={artwork.image_url}
              alt={artwork.title}
              className="w-full h-full object-cover"
              data-testid="artwork-detail-image"
            />
          </div>

          <div className="md:sticky md:top-32">
            <div className="mb-6">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                {artwork.category}
              </p>
              <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-4" data-testid="artwork-detail-title">
                {artwork.title}
              </h1>
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                BY {artwork.artist_name}
              </p>
            </div>

            <div className="border-t border-border pt-6 mb-6">
              <p className="text-3xl font-medium mb-2" data-testid="artwork-detail-price">
                ${artwork.price.toFixed(2)}
              </p>
              <p className="text-sm text-muted-foreground">+ ${artwork.shipping_rate?.toFixed(2) || '15.00'} shipping</p>
            </div>

            <div className="mb-8">
              <p className="text-base md:text-lg leading-relaxed whitespace-pre-line" data-testid="artwork-detail-description">
                {artwork.description}
              </p>
            </div>

            <div className="mb-8 border-t border-border pt-6">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                DIMENSIONS
              </p>
              <p className="text-base" data-testid="artwork-detail-dimensions">{artwork.dimensions}</p>
              
              {/* Availability display - different for artist vs customers */}
              {isArtist ? (
                /* Artist sees exact quantity */
                <div className="mt-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                    STOCK
                  </p>
                  <p className="text-base">{artwork.quantity_available || 0} available</p>
                </div>
              ) : (
                /* Customers see limited info */
                <>
                  {(artwork.artwork_status === 'sold' || artwork.quantity_available === 0) && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-red-500">SOLD OUT</p>
                    </div>
                  )}
                  {artwork.artwork_status === 'reserved' && artwork.quantity_available > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-amber-600">RESERVED</p>
                    </div>
                  )}
                  {artwork.artwork_status !== 'reserved' && artwork.artwork_status !== 'sold' && artwork.quantity_available === 1 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-accent">Only 1 piece available</p>
                    </div>
                  )}
                  {/* If quantity > 1, don't show anything to customers */}
                </>
              )}
            </div>

            {!isArtist && (
              <div className="flex gap-4">
                {artwork.is_available && artwork.artwork_status !== 'reserved' && artwork.artwork_status !== 'sold' && artwork.quantity_available > 0 ? (
                  <>
                    <Button
                      size="lg"
                      onClick={handleAddToCart}
                      className="flex-1 rounded-full px-8 py-6 text-sm uppercase tracking-widest hover:scale-105 transition-transform"
                      data-testid="add-to-cart-detail-btn"
                    >
                      ADD TO CART
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={handleToggleFavorite}
                      className="rounded-full px-6"
                      data-testid="favorite-detail-btn"
                    >
                      <Heart className={`h-5 w-5 ${isFavorited ? 'fill-accent text-accent' : ''}`} />
                    </Button>
                  </>
                ) : (
                  <div className="w-full text-center py-6 bg-secondary rounded-full">
                    <span className="text-sm uppercase tracking-[0.2em] text-muted-foreground">
                      SOLD OUT
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="mt-8 border-t border-border pt-6">
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
                ABOUT THIS PIECE
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• Original artwork, one of a kind</li>
                {artwork.is_signed && <li>• Signed by the artist</li>}
                {artwork.includes_certificate && <li>• Certificate of authenticity included</li>}
                {artwork.is_framed && <li>• Includes frame</li>}
                <li>• Securely packaged and shipped</li>
                <li>• Flat rate shipping: ${artwork.shipping_rate?.toFixed(2) || '15.00'}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}